import React, {Fragment, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {StorefrontOutlined} from "@mui/icons-material";
import {Col} from "reactstrap";
import classnames from "classnames";
import {Box, Tooltip} from "@mui/material";
import ToggleButtons from "../../../../../../../../components/base/button/toggle_buttons";
import ConfigurableInfoBlock, {ConfigurableInfoBlockInputTypes} from "../../../../../../../../components/app-specific/configurable-info-block";
import {formatMoney} from "../../../../../../../../../core/services/utils/utils";
import {ReactComponent as PriceIncreaseIcon} from "../../../../../../../../../assets/images/price-inflation-icons/price-increase-icon.svg";
import {ReactComponent as PriceDecreaseIcon} from "../../../../../../../../../assets/images/price-inflation-icons/price-decrease-icon.svg";
import ProductEntryHistoryDialog from "../../../../../../../../components/dialogs/product-entry-history";
import Slider from "react-slick";
import {ReactComponent as LeftChevron} from "../../../../../../../../../assets/images/filled-chevron/left.svg";
import {ReactComponent as RightChevron} from "../../../../../../../../../assets/images/filled-chevron/right.svg";
import {ReactComponent as Arrow} from "../../../../../../../../../assets/images/price-change-arrow.svg";
import DateUtils from "../../../../../../../../../core/services/date-utils";
import {isFormula} from "../../../../../../../../../core/services/formula-engine";
import FormulaDrivenPriceAdornment from "../../../../../../../../components/app-specific/configurable-info-block/input/formula-input/formula-driven-price-adornment";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import CompetitorPriceReportsPopover from "../../../../../../../../components/app-specific/draggable-popover/competitor-price-reports-popover";


const PriceHistoryColumn = ({
                                priceHistory,
                                getProductPricesForChart,
                                getProductPricesForExport,
                                live,
                                pinned,
                                togglePinnedState,
                                partNo,
                                partId,
                                loading,
                                activeFormulas,
                                resolvedValues,
                                specialResolvedValues,
                                allFormulaErrors,
                                onFormulaChanged,
                                allowFormulas,
                                showFormulas = true,
                                changePrice,
                                disableInteractions = false,
                            }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.product-prices'});
    const operatorAccessLevels = useAccessLevels();
    const canExportProductPriceHistory = operatorAccessLevels.packages.lookup.exportProductPriceHistory;
    const canViewAllCompetitorPriceReports = operatorAccessLevels.packages.lookup.viewAllCompetitorPriceReports;
    const canViewMyCompetitorPriceReports = operatorAccessLevels.packages.lookup.viewMyCompetitorPriceReports;
    const canOpenCompetitorPriceReports = canViewAllCompetitorPriceReports || canViewMyCompetitorPriceReports;
    const competitorPriceReportsMineOnly = !canViewAllCompetitorPriceReports && canViewMyCompetitorPriceReports;
    const [chartInfo, setChartInfo] = useState({open: false, label: '', id: null});
    const [editingFormulaPriceLevelId, setEditingFormulaPriceLevelId] = useState(null);

    useEffect(() => {
        if (showFormulas || disableInteractions) {
            setEditingFormulaPriceLevelId(null);
        }
    }, [showFormulas, disableInteractions]);

    const isQuickEditingFormula = useCallback((priceLevelId) => (
        live && allowFormulas && !showFormulas && editingFormulaPriceLevelId === priceLevelId
    ), [live, allowFormulas, showFormulas, editingFormulaPriceLevelId]);

    const startQuickFormulaEdit = useCallback((priceLevelId) => {
        if (editingFormulaPriceLevelId != null && editingFormulaPriceLevelId !== priceLevelId) {
            document.activeElement?.blur();
            setTimeout(() => setEditingFormulaPriceLevelId(priceLevelId), 0);
            return;
        }
        setEditingFormulaPriceLevelId(priceLevelId);
    }, [editingFormulaPriceLevelId]);

    const endQuickFormulaEdit = useCallback(() => {
        if (!showFormulas) {
            setEditingFormulaPriceLevelId(null);
        }
    }, [showFormulas]);

    /**
     * Fetches the product's history data from the server and then picks the data for this specific price level
     *
     * @param filters
     * @return {Promise<{[p: string]: *}|*>}
     */
    const getPriceHistoryChartData = async (filters) => {
        const response = await getProductPricesForChart(filters);
        if (!response?.resultFlag) {
            return response;
        }

        return {
            ...response,
            data: [{
                ...response?.data?.find(e => e.id === chartInfo.id),
                options: {},
            }]
        }
    }

    const buttonOptions = useMemo(() => ([{
        value: priceHistory,
        name: (
            <p>
                {pinned ? "UNPIN " : "PIN "}
                {DateUtils.serverToLocal(priceHistory?.modifiedDateTime).format(DateUtils.dateFormatAlternative)}
            </p>
        ),
    }]), [priceHistory, pinned])

    /**
     * Fetches the price level history of the current product to be used for exporting purposes.
     *
     * @param {any} filters
     * @return {Promise<{data: {dateLabel: *, title: string, content: *[]}, resultFlag: boolean}|{data: (*[]|undefined), resultFlag: boolean}>}
     */
    const getPriceHistoryChartDataForExport = async (filters) => {
        const response = await getProductPricesForExport(filters);
        if (!response.resultFlag) {
            // just pass it along since nothing will be generated
            return response;
        }
        const title = `Prices of ${partNo ?? ''}'s ${chartInfo.label}`;
        let _response = null;
        if (chartInfo?.id !== undefined && chartInfo?.id !== null) {
            _response = response.data.filter(e => e.priceLevel.id === chartInfo?.id);
        }
        const __response = _response ?? response.data
        const data = __response?.map((e, index) => ({
            "": index + 1,
            "Old Price": e.oldPrice ? formatMoney(e.oldPrice) : '--',
            "New Price": e.newPrice ? formatMoney(e.newPrice) : '--',
            "Submitted Date": DateUtils.serverToLocal(e.submittedDate).format(DateUtils.dateTimeFormat) ?? '--',
            "Operator": e.operatorName ?? '--',
            "Price Level": e.priceLevel?.title ?? '--',
        })) ?? [];
        return {
            resultFlag: response?.resultFlag,
            data: data,
            title: title,
        };
    }

    return (
        <>
            <Col xs={12} md={6} xl={4} className={classnames('price-history-column', {live})}>
                {
                    !live
                        ? <ToggleButtons
                            className={'pin-button'}
                            value={pinned ? priceHistory : undefined}
                            onChange={() => togglePinnedState(priceHistory)}

                            options={buttonOptions}
                        />
                        : <div className={'live-price-indicator'}>
                            <div>
                                <p>Live Price</p>
                                <p>Min Price</p>
                            </div>
                        </div>
                }
                {
                    loading
                        ? <div>
                            {
                                Array(15).fill(null).map((_, e) => (
                                    <div key={e} className={'loading normal mb-4 pr-5'}>
                                        <div/>
                                    </div>
                                ))
                            }
                        </div>
                        : priceHistory?.prices?.map((priceEntry) => {
                            const priceLevelId = priceEntry?.priceLevel?.id;
                            const activeFormulaObj = activeFormulas?.find(f => f.priceLevelId === priceLevelId);
                            const hasActiveFormula = !!(activeFormulaObj && isFormula(activeFormulaObj.formula));
                            const quickEditingFormula = isQuickEditingFormula(priceLevelId);
                            const rowShowsFormulaEditor = allowFormulas && (showFormulas || quickEditingFormula);
                            const showFormulaAdornment = allowFormulas && !showFormulas && hasActiveFormula && !quickEditingFormula;

                            const displayValue = rowShowsFormulaEditor
                                ? (activeFormulaObj?.formula ?? priceEntry?.price?.toString() ?? '')
                                : String(
                                    hasActiveFormula
                                        ? (resolvedValues[priceLevelId] ?? priceEntry?.price ?? '')
                                        : (priceEntry?.price ?? ''),
                                );

                            if (!live) {
                                return (
                                    <PreviousPriceContainer
                                        key={priceEntry[0].id}
                                        className={classnames('price-block')}
                                        title={priceEntry[0].title}
                                        prices={priceEntry}
                                    />
                                );
                            } else {
                                const isDisabled = !live || disableInteractions;
                                const block = (
                                    <ConfigurableInfoBlock
                                        key={priceEntry?.priceLevel?.id}
                                        className={classnames('price-block', {
                                            "disabled": isDisabled,
                                            "increased": priceEntry?.hasIncreased,
                                            "decreased": priceEntry?.hasDecreased
                                        })}
                                        title={priceEntry?.priceLevel?.title}
                                        titleFullWidth
                                        showCustomIcons
                                        customIcons={[
                                            ...(priceEntry?.hasIncreased ? [<PriceIncreaseIcon className={'increase-icon'}/>] : []),
                                            ...(priceEntry?.hasDecreased ? [<PriceDecreaseIcon className={'decrease-icon'}/>] : []),
                                            ...(live && canOpenCompetitorPriceReports && partId ? [
                                                <Tooltip title={t('viewCompetitorReports')} key={'competitor-reports'}>
                                                    <Box component={'span'} sx={{display: 'inline-flex'}}>
                                                        <CompetitorPriceReportsPopover
                                                            partId={partId}
                                                            partNumber={partNo}
                                                            priceLevelId={priceEntry?.priceLevel?.id}
                                                            priceLevelTitle={priceEntry?.priceLevel?.title}
                                                            mineOnly={competitorPriceReportsMineOnly}
                                                        >
                                                            <StorefrontOutlined fontSize={'small'}/>
                                                        </CompetitorPriceReportsPopover>
                                                    </Box>
                                                </Tooltip>,
                                            ] : []),
                                        ]}
                                        showChartIcon={!isDisabled}
                                        inputType={rowShowsFormulaEditor
                                            ? ConfigurableInfoBlockInputTypes.formula
                                            : ConfigurableInfoBlockInputTypes.input}
                                        inputProps={rowShowsFormulaEditor ? {
                                            value: displayValue,
                                            priceLevels: priceHistory?.prices?.map(p => p.priceLevel) ?? [],
                                            resolvedValues: resolvedValues,
                                            specialResolvedValues: specialResolvedValues,
                                            circularError: allFormulaErrors[priceLevelId] ?? null,
                                            label: priceEntry?.priceLevel?.title,
                                            disabled: isDisabled,
                                            autoFocus: quickEditingFormula,
                                            onBlur: quickEditingFormula ? endQuickFormulaEdit : undefined,
                                            onKeyDown: quickEditingFormula
                                                ? (e) => {
                                                    if (e.key === 'Escape') {
                                                        e.preventDefault();
                                                        endQuickFormulaEdit();
                                                    }
                                                }
                                                : undefined,
                                            inputProps: {
                                                min: Number(priceEntry?.minimumPrice ?? '0'),
                                            },
                                        } : {
                                            value: displayValue,
                                            type: 'number',
                                            disabled: isDisabled,
                                            inputProps: {
                                                min: Number(priceEntry?.minimumPrice ?? '0'),
                                            },
                                            ...(showFormulaAdornment && {
                                                InputProps: {
                                                    endAdornment: (
                                                        <FormulaDrivenPriceAdornment
                                                            formula={activeFormulaObj.formula}
                                                            resolvedValue={resolvedValues[priceLevelId]}
                                                            circularError={allFormulaErrors[priceLevelId] ?? null}
                                                            onEditFormula={() => startQuickFormulaEdit(priceLevelId)}
                                                            editDisabled={isDisabled}
                                                        />
                                                    ),
                                                },
                                            }),
                                        }}
                                        onChartIconClicked={() => setChartInfo({
                                            label: priceEntry?.priceLevel?.title,
                                            id: priceLevelId,
                                            open: true,
                                        })}
                                        onValueChanged={allowFormulas
                                            ? (value) => onFormulaChanged(priceLevelId, value)
                                            : (value) => changePrice(priceLevelId, parseFloat(value))
                                        }
                                        additionalInfo={
                                            <p className={'info-block-min-price mt-2'}>
                                                {formatMoney(priceEntry?.minimumPrice ?? '') === '$0.00' ? '' : formatMoney(priceEntry?.minimumPrice ?? '')}
                                            </p>
                                        }
                                    />
                                );

                                return disableInteractions ? (
                                    <Tooltip title="Please resolve formula discrepancies first" key={priceEntry?.priceLevel?.id} disableInteractive>
                                        <Box component={'span'} sx={{width: '100%', cursor: 'not-allowed'}}>
                                            <Box sx={{pointerEvents: 'none'}}>
                                                {block}
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                ) : block;
                            }
                        })
                }
            </Col>
            <ProductEntryHistoryDialog
                title={`Product ${chartInfo?.label ?? ''} History`}
                open={chartInfo.open}
                close={() => setChartInfo({})}
                getHistory={getPriceHistoryChartData}
                getHistoryForExport={getPriceHistoryChartDataForExport}
                hideExport={!canExportProductPriceHistory}
            />
        </>
    );

}


const PreviousPriceContainer = ({title, prices}) => {
    const [priceList, setPriceList] = useState(prices);
    const [selectedIndex, setSelectedIndex] = useState(0);
    /**@type {React.MutableRefObject<import('@types/react-slick').Slider>}*/
    const slickSliderRef = useRef();

    const showChevrons = useMemo(() => (priceList?.length ?? 0) > 1, [priceList?.length])

    /**
     * Prior to rendering the component:
     * Sets the [selectedPrice] in the inner state to the first item in the prices prop
     */
    useEffect(() => {
        setPriceList(prices);
    }, [prices])


    /**
     * After each change in the slide, sets the [selectedPrice] to the received number
     * @param index
     */
    const afterSlideChange = (index) => {
        setSelectedIndex(index);
    }

    /**
     * Handles scrolling the slick based on the received index
     * @param {number} index
     */
    const onChevronClicked = (index) => {
        if (index > 0) {
            slickSliderRef.current.slickNext();
            return;
        }
        slickSliderRef.current.slickPrev();
    }


    /**
     * Memo version of the icon to be shown on top of the container
     * @type {React.JSX.Element[]}
     */
    const icons = useMemo(() => [
        ...(priceList[selectedIndex]?.hasIncreased ? [
            <Box sx={{width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <PriceIncreaseIcon key={'increase-icon'} className={'increase-icon'}/>
            </Box>
        ] : []),
        ...(priceList[selectedIndex]?.hasDecreased ? [
            <Box sx={{width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <PriceDecreaseIcon key={'decrease-icon'} className={'decrease-icon'}/>
            </Box>
        ] : []),
    ], [priceList, selectedIndex])

    return (
        <div className={classnames('previous-price-container')}>
            <div className={'d-flex flex-row justify-content-between w-100'}>
                <div className={classnames('title-section')}>
                    <p className={'title'}>
                        {title ?? ''}
                    </p>
                    <div className={'d-flex align-items-center'}>
                        {icons}
                    </div>
                </div>
                <div/>
            </div>
            <div className={'main-container'}>
                {
                    showChevrons &&
                    <div
                        className={classnames('chevron left', {'inactive': selectedIndex === 0})}
                        onClick={selectedIndex === 0
                            ? () => void 0
                            : () => onChevronClicked(-1)
                        }
                    >
                        <LeftChevron/>
                    </div>
                }
                <div className={'slider'}>
                    <Slider
                        ref={slickSliderRef}
                        autoplay={false}
                        infinite={false}
                        initialSlide={0}
                        afterChange={afterSlideChange}
                        slidesToShow={1}
                    >
                        {
                            priceList.map((e) => (
                                <div key={e.id ?? -1} className={'price'}>
                                    <div className={classnames(
                                        'inner',
                                        {'increased': e.hasIncreased},
                                        {'decreased': e.hasDecreased},
                                        {'cursor-default-hover': e.amount === null}
                                    )}>
                                        <p className={classnames('amount', {'not-changed': e.amount === null})}>
                                            {
                                                e.amount === null
                                                    ? <p>Not Changed</p>
                                                    : <>
                                                        <Tooltip title={`${e.prevAmount === null ? 'Not Changed' : formatMoney(e.prevAmount)}`}>
                                                            <span>
                                                            {`${e.prevAmount === null ? 'Not Changed' : formatMoney(e.prevAmount)}`}
                                                        </span>
                                                        </Tooltip>
                                                        <Arrow/>
                                                        <Tooltip title={`${e.amount === null ? 'Not Changed' : formatMoney(e.amount)}`}>
                                                            <span>{`${e.amount === null ? 'Not Changed' : formatMoney(e.amount)}`}</span>
                                                        </Tooltip>
                                                    </>
                                            }
                                        </p>
                                        <div className={'time-container'}>
                                            <p>
                                                {`${e.date === null ? '' : DateUtils.serverToLocal(e.date).format("hh:mm A")}`}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        }
                    </Slider>
                </div>
                {
                    showChevrons &&
                    <div
                        className={classnames('chevron right', {'inactive': selectedIndex === (priceList.length - 1)})}
                        onClick={selectedIndex === (priceList.length - 1)
                            ? () => void 0
                            : () => onChevronClicked(1)}
                    >
                        <RightChevron/>
                    </div>
                }
            </div>
        </div>
    );
}

export default PriceHistoryColumn;
